import { Modal } from '@skupreme/shared/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import MetricImperialToggle from '@components/MetricImperialToggle';
import { ErrorMessageConstant } from '@utility/constants/common';
import { CommonUtility } from '@utility/common';
import { ToastMessage } from '@utility/toast';
import { LoaderBar } from '@components/LoaderBar';
import * as yup from 'yup';
import { ComponentsService } from '@utility/services/components';
import { FlexRow, JustifyBetweenRow } from '@elements/Common';
import { DeleteConfirmWithButton } from '@components/DeleteConfirm';
import { useComponentsMetadata } from '@hooks/components';
import { ConversionUtility } from '@utility/conversions';
import {
  AddEditAttributeFormElement,
  AttributeSchema,
  convertFieldsToImperial,
  convertFieldsToMetric,
} from './FormElement';

const FormSchema = yup.object().shape(AttributeSchema);

export function AddEditComponentAttributePopup({
  open,
  imperial,
  closeModal,
  currentAlias,
  componentId,
  refetch,
  skipSave,
  attributes,
  addByTextbox,
  fetchAttribute,
  hideAction,
  allowAdditions = true,
  currentComponent,
}) {
  const { options: optionsComponentsMetadata } = useComponentsMetadata();

  const [imperialForm, setImperialForm] = useState(false);
  const [processing, setProcessing] = useState('');
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [popupAttributes, setPopupAttributes] = useState([]);

  const { weightText, heightText } = useMemo(
    () => ({
      heightText: imperialForm ? 'inch' : 'cm',
      weightText: imperialForm ? 'lb' : 'kg',
      heightTooltipText: imperialForm ? 'inches' : 'cm',
    }),
    [imperialForm],
  );

  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    getValues,
    trigger,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(FormSchema),
    shouldUnregister: true,
    mode: 'onChange',
  });

  // @ts-expect-error - TS2769: No overload matches this call.
  const carton_gross_weight = watch('carton_gross_weight');
  // @ts-expect-error - TS2769: No overload matches this call.
  const carton_net_weight = watch('carton_net_weight');
  // @ts-expect-error - TS2769: No overload matches this call.
  const units_per_carton = watch('units_per_carton');
  // @ts-expect-error - TS2769: No overload matches this call.
  const unitOfMeasurement = watch('unit_of_measurement');
  // @ts-expect-error - TS2769: No overload matches this call.
  const aliasName = watch('alias');

  const { isDuplicate, canTakeAction, isOld } = useMemo(
    () => ({
      isDuplicate:
        addByTextbox &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        (popupAttributes || []).some(x => x.alias === aliasName),
      canTakeAction:
        !hideAction &&
        aliasName &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        !(popupAttributes || []).find(x => x.alias === aliasName)?.primary,
      isOld:
        !hideAction &&
        aliasName &&
        // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
        (popupAttributes || []).some(x => x.alias === aliasName),
    }),
    [addByTextbox, popupAttributes, aliasName, hideAction],
  );

  useEffect(() => {
    if (
      CommonUtility.isNotEmpty(carton_net_weight) &&
      CommonUtility.isNotEmpty(carton_gross_weight) &&
      // @ts-expect-error - TS2345: Argument of type 'readonly never[]' is not assignable to parameter of type 'numb...
      !isNaN(carton_net_weight) &&
      // @ts-expect-error - TS2345: Argument of type 'readonly never[]' is not assignable to parameter of type 'numb...
      !isNaN(carton_gross_weight)
    ) {
      // @ts-expect-error - TS2345: Argument of type '"carton_net_weight"' is not assignable to parameter of type 'n...
      trigger('carton_net_weight');
    }
  }, [carton_gross_weight]);

  useEffect(() => {
    const setAttribute = async () => {
      setImperialForm(imperial);
      let temp = [...attributes];
      if (fetchAttribute && !currentAlias && (attributes || []).length > 0) {
        try {
          setProcessing('Loading');
          const result = await ComponentsService.attributes(componentId);
          temp = [...(result?.data || [])];
        } finally {
          setProcessing('');
        }
      }
      // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
      setPopupAttributes([...temp]);
      const options = temp.map(option => ({
        value: option.alias,
        text: option.alias,
      }));
      // @ts-expect-error - TS2345: Argument of type '{ value: any; text: any; }[]' is not assignable to parameter o...
      setDropdownOptions(options);
      if (currentAlias) {
        setFormData(
          temp.find(x => x.alias === currentAlias),
          imperial,
        );
      }
    };

    if (open) {
      setAttribute();
    } else {
      reset({});
      setDropdownOptions([]);
      setPopupAttributes([]);
    }
  }, [open, currentAlias, imperial, attributes, fetchAttribute]);

  function refactorNumber(value) {
    if (CommonUtility.isNotEmpty(value)) {
      return CommonUtility.roundNumber(value, 5);
    }
    return '';
  }

  const setFormData = (attribute, imperial) => {
    const convertWith = imperial
      ? ConversionUtility.convertItemToImperial
      : ConversionUtility.convertItemToMetric;
    const temp = convertWith(attribute);
    const formValues = {
      carton_gross_weight: refactorNumber(temp.carton_gross_weight?.value),
      carton_height: refactorNumber(temp.carton_height?.value),
      carton_length: refactorNumber(temp.carton_length?.value),
      carton_net_weight: refactorNumber(temp.carton_net_weight?.value),
      carton_width: refactorNumber(temp.carton_width?.value),
      units_per_carton: temp.units_per_carton,
      unit_of_measurement: temp?.unit_of_measurement || 'pieces',
      alias: temp.alias,
    };

    if (imperialForm) {
      const updated = CommonUtility.convertOrderUnit(true, {
        // @ts-expect-error - TS2322: Type 'AttributeValue' is not assignable to type 'string'.
        unitOfMeasurement: formValues?.unit_of_measurement || 'pieces',
        optionsComponentsMetadata,
        // @ts-expect-error - TS2322: Type 'AttributeValue' is not assignable to type 'number'.
        value: formValues.units_per_carton,
      });

      // @ts-expect-error - TS2322: Type 'number' is not assignable to type 'AttributeValue'.
      formValues.units_per_carton = updated > 0 ? Math.round(updated) : 0;
    }

    // @ts-expect-error - TS2345: Argument of type '{ carton_gross_weight: string | number; carton_height: string ...
    reset({
      ...formValues,
    });
  };

  const save = async formData => {
    if (isDuplicate) {
      return;
    }
    setProcessing('Saving');
    const attribute = {
      carton_gross_weight: {
        value: formData.carton_gross_weight,
        unit: weightText,
      },
      carton_height: {
        value: formData.carton_height,
        unit: heightText,
      },
      carton_length: {
        value: formData.carton_length,
        unit: heightText,
      },
      carton_net_weight: {
        value: formData.carton_net_weight,
        unit: weightText,
      },
      carton_width: {
        value: formData.carton_width,
        unit: heightText,
      },
      units_per_carton: formData.units_per_carton,
      unit_of_measurement: formData?.unit_of_measurement || 'pieces',
      alias: formData.alias,
    };

    try {
      if (!skipSave) {
        await ComponentsService.updateAttributes(componentId, {
          // @ts-expect-error - TS2345: Argument of type '{ carton_gross_weight: { value: any; unit: string; }; carton_h...
          attributes: [ConversionUtility.convertItemToMetric(attribute)],
        });

        ToastMessage.success('Carton Configuration updated successfully.');
      }
      if (refetch) {
        await refetch(attribute);
      }
      let convertedAttribute = attribute;
      if (imperial) {
        // @ts-expect-error - TS2740: Type 'AttributeMap' is missing the following properties from type '{ carton_gros...
        convertedAttribute = ConversionUtility.convertItemToImperial(attribute);
      } else {
        // @ts-expect-error - TS2740: Type 'AttributeMap' is missing the following properties from type '{ carton_gros...
        convertedAttribute = ConversionUtility.convertItemToMetric(attribute);
      }
      closeModal(convertedAttribute);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  const changeUnits = () => {
    const watchFields = getValues();
    let newFormData;
    if (!imperialForm) {
      newFormData = convertFieldsToImperial(
        watchFields,
        optionsComponentsMetadata,
      );
    } else {
      newFormData = convertFieldsToMetric(
        watchFields,
        optionsComponentsMetadata,
      );
    }
    reset(newFormData);
    setImperialForm(!imperialForm);
  };

  const modalClose = () => {
    closeModal();
  };

  const aliasDropdownChanges = ({ value }) => {
    if (value.trim()) {
      // @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never'.
      const tempAttribute = popupAttributes.find(x => x.alias === value);
      if (tempAttribute) {
        setFormData(tempAttribute, imperialForm);
      } else {
        // @ts-expect-error - TS2345: Argument of type '(options: never[]) => { key: any; value: any; text: any; item:...
        setDropdownOptions(options => [
          ...options,
          {
            key: value,
            value,
            text: value,
            item: {},
          },
        ]);
        setFormData(
          {
            carton_gross_weight: {
              value: '',
              unit: weightText,
            },
            carton_height: {
              value: '',
              unit: heightText,
            },
            carton_length: {
              value: '',
              unit: heightText,
            },
            carton_net_weight: {
              value: '',
              unit: weightText,
            },
            carton_width: {
              value: '',
              unit: heightText,
            },
            units_per_carton: '',
            unit_of_measurement: 'pieces',
            alias: value,
          },
          imperialForm,
        );
      }
    }
  };

  const markAsPrimary = async () => {
    try {
      setProcessing('Updating');
      // @ts-expect-error - TS2554: Expected 3 arguments, but got 2.
      await ComponentsService.itemAliasMarkPrimary(aliasName, {
        component_id: componentId,
        primary: true,
      });
      if (refetch) {
        await refetch();
      }
      modalClose();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  const deleteAttribute = async () => {
    try {
      setProcessing('Deleting');
      await ComponentsService.deleteAttribute(componentId, aliasName);

      ToastMessage.success('Item Carton Configuration deleted successfully.');
      if (refetch) {
        await refetch();
      }
      modalClose();
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  const class_of_measurement = CommonUtility.getClassOfMeasurement(
    currentComponent?.class_of_measurement,
    currentComponent?.unit_of_measurement,
    optionsComponentsMetadata,
  );

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && modalClose()}
      size="md"
      isDismissable={false}
      title={`${currentAlias ? 'Edit' : 'Add'} ${aliasName ?? ''}`}
      className="sk:max-h-full sk:overflow-y-auto"
    >
      {processing && <LoaderBar content={processing} />}
      <MetricImperialToggle
        toggle
        onChange={() => changeUnits()}
        checked={imperialForm}
        className="sk:mx-3.5!"
        wrapperClassName="sk:mt-2! sk:mb-3.5!"
      />
      <form noValidate onSubmit={handleSubmit(save)}>
        <AddEditAttributeFormElement
          addByTextbox={addByTextbox}
          aliasDropdownChanges={aliasDropdownChanges}
          control={control}
          currentAlias={currentAlias}
          dropdownOptions={dropdownOptions}
          errors={errors}
          imperialForm={imperialForm}
          isDuplicate={isDuplicate}
          allowAdditions={allowAdditions}
          unitText={class_of_measurement}
          units_per_carton={units_per_carton}
          // @ts-expect-error - TS2322: Type 'readonly never[]' is not assignable to type 'string'.
          unit={unitOfMeasurement}
          optionsComponentsMetadata={optionsComponentsMetadata}
          setValue={setValue}
          currentUnit={class_of_measurement}
          unitFieldName="unit_of_measurement"
        />
        <JustifyBetweenRow
          className={canTakeAction && isOld ? '' : 'sk:justify-end! sk:mt-2!'}
        >
          {canTakeAction && isOld && (
            <FlexRow>
              <HoverBorderButton onClick={markAsPrimary} type="button">
                Mark as Primary
              </HoverBorderButton>
              <DeleteConfirmWithButton onConfirm={deleteAttribute} />
            </FlexRow>
          )}
          <FlexRow>
            <HoverBorderButton onClick={modalClose} type="button">
              Cancel
            </HoverBorderButton>
            <StyledButton className="sk:ml-2!" disabled={isDuplicate}>
              Save
            </StyledButton>
          </FlexRow>
        </JustifyBetweenRow>
      </form>
    </Modal>
  );
}
